import * as React from "react";
import { useCallback, useContext, useMemo, useState } from "react";

import type { ChatTranscriptItem, ReferencedFile } from "~/ai-chat/model/chat-message";

interface AiChatContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  messages: ChatTranscriptItem[];
  appendMessage: (message: ChatTranscriptItem) => void;
  updateMessage: (id: string, updater: (item: ChatTranscriptItem) => ChatTranscriptItem) => void;
  clearMessages: () => void;
  isAvailable: boolean;
  markUnavailable: () => void;
  hasMcpTools: boolean;
  markMcpToolsAvailable: () => void;
  referencedFiles: ReferencedFile[];
  addReferencedFiles: (files: ReferencedFile[]) => void;
  removeReferencedFile: (resourceId: string) => void;
  clearReferencedFiles: () => void;
}

const AiChatContext = React.createContext<AiChatContextValue | null>(null);

export function useAiChat() {
  const context = useContext(AiChatContext);
  if (!context) {
    throw new Error("useAiChat must be used within an AiChatProvider.");
  }
  return context;
}

export function useAiChatOptional() {
  return useContext(AiChatContext);
}

interface AiChatProviderProps {
  children: React.ReactNode;
  defaultOpen?: boolean;
}

export function AiChatProvider({ children, defaultOpen = false }: AiChatProviderProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [messages, setMessages] = useState<ChatTranscriptItem[]>([]);
  const [isAvailable, setIsAvailable] = useState(true);
  const [hasMcpTools, setHasMcpTools] = useState(false);
  const [referencedFiles, setReferencedFiles] = useState<ReferencedFile[]>([]);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  const appendMessage = useCallback((message: ChatTranscriptItem) => {
    setMessages((prev) => [...prev, message]);
  }, []);

  // Used by streaming to grow an in-flight assistant message as deltas arrive.
  const updateMessage = useCallback((id: string, updater: (item: ChatTranscriptItem) => ChatTranscriptItem) => {
    setMessages((prev) => prev.map((item) => (item.id === id ? updater(item) : item)));
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setReferencedFiles([]);
  }, []);

  const markUnavailable = useCallback(() => setIsAvailable(false), []);
  const markMcpToolsAvailable = useCallback(() => setHasMcpTools(true), []);

  const addReferencedFiles = useCallback((files: ReferencedFile[]) => {
    setReferencedFiles((prev) => {
      const byId = new Map(prev.map((file) => [file.resourceId, file]));
      for (const file of files) {
        byId.set(file.resourceId, file);
      }
      return [...byId.values()];
    });
  }, []);

  const removeReferencedFile = useCallback((resourceId: string) => {
    setReferencedFiles((prev) => prev.filter((file) => file.resourceId !== resourceId));
  }, []);

  const clearReferencedFiles = useCallback(() => setReferencedFiles([]), []);

  const value = useMemo<AiChatContextValue>(
    () => ({
      isOpen,
      open,
      close,
      toggle,
      messages,
      appendMessage,
      updateMessage,
      clearMessages,
      isAvailable,
      markUnavailable,
      hasMcpTools,
      markMcpToolsAvailable,
      referencedFiles,
      addReferencedFiles,
      removeReferencedFile,
      clearReferencedFiles,
    }),
    [
      isOpen,
      open,
      close,
      toggle,
      messages,
      appendMessage,
      updateMessage,
      clearMessages,
      isAvailable,
      markUnavailable,
      hasMcpTools,
      markMcpToolsAvailable,
      referencedFiles,
      addReferencedFiles,
      removeReferencedFile,
      clearReferencedFiles,
    ],
  );

  return <AiChatContext.Provider value={value}>{children}</AiChatContext.Provider>;
}
